import API_BASE from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'May 2025';

const Card = ({ children, style = {} }) => (
  <div style={{ background: '#ffffff', border: '1px solid #E5E9F0', borderRadius: '1rem', padding: '1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', ...style }}>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h2 style={{ color: '#0D1F3C', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.75rem' }}>{title}</h2>
    <div style={{ color: '#374151', lineHeight: 1.75, fontSize: '0.9375rem' }}>{children}</div>
  </div>
);

const P = ({ children }) => <p style={{ marginBottom: '0.875rem' }}>{children}</p>;

const BulletList = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 1rem' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <span style={{ color: '#1A6FD6', fontWeight: 700, flexShrink: 0, marginTop: '0.1rem' }}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Pricing = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({ price: 999, standard_price: 999, offer_price: null, is_offer_active: false });

  useEffect(() => {
    fetch(`${API_BASE}/api/pricing/current`)
      .then(r => r.json())
      .then(d => setPricing(d))
      .catch(() => {});
  }, []);

  return (
    <div style={{ background: '#FFFBEB', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      <div style={{ background: '#ffffff', borderBottom: '1px solid #E5E9F0', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontWeight: 600, fontSize: '0.875rem' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Successlane
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1A6FD6' }}></div>
            <span style={{ fontWeight: 700, color: '#0D1F3C', fontSize: '0.9rem' }}>Successlane</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>

        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1A6FD6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Pricing</p>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C', marginBottom: '0.5rem', lineHeight: 1.2 }}>Simple, transparent pricing</h1>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Operated by <strong>Success Lane</strong></p>
        </div>

        {/* Plan cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>

          {/* Free Evaluation */}
          <Card>
            <div style={{ display: 'inline-block', background: '#EFF6FF', color: '#1A6FD6', fontSize: '0.7rem', fontWeight: 700, padding: '0.25rem 0.625rem', borderRadius: '9999px', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Free</div>
            <h3 style={{ color: '#0D1F3C', fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>LinkedIn Evaluation</h3>
            <p style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C', margin: '0.5rem 0' }}>
              ₹0 <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6B7280' }}>· one-time</span>
            </p>
            <p style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>A recruiter-voice assessment of your current LinkedIn profile. AI-assisted, calibrated with active recruiter inputs.</p>
            <BulletList items={[
              'Headline, summary, experience reviewed',
              'Keyword-gap analysis for your target role',
              'Delivered as a written assessment',
              'No credit card required',
            ]} />
            <button onClick={() => navigate('/analyze')} style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #1A6FD6', background: 'white', color: '#1A6FD6', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              Start free evaluation
            </button>
          </Card>

          {/* Paid Rewrite */}
          <Card style={{ borderColor: '#F59E0B', borderWidth: '2px' }}>
            <div style={{ display: 'inline-block', background: '#FEF3C7', color: '#92400E', fontSize: '0.7rem', fontWeight: 700, padding: '0.25rem 0.625rem', borderRadius: '9999px', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Most popular</div>
            <h3 style={{ color: '#0D1F3C', fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>LinkedIn Rewrite + Recruiter Certification</h3>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
              {pricing.is_offer_active ? (
                <>
                  <span style={{ fontSize: '2rem', fontWeight: 800, color: '#1A6FD6' }}>₹{pricing.offer_price}</span>
                  <span style={{ fontSize: '1.25rem', fontWeight: 500, color: '#6B7280', textDecoration: 'line-through' }}>₹{pricing.standard_price}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, background: '#FEF3C7', color: '#92400E', padding: '0.15rem 0.5rem', borderRadius: '999px' }}>
                    Save ₹{pricing.standard_price - pricing.offer_price}
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C' }}>₹{pricing.standard_price}</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#6B7280' }}>· one-time</span>
                </>
              )}
            </div>
            <p style={{ color: '#6B7280', fontSize: '0.8125rem', marginBottom: '1rem' }}>Inclusive of all applicable taxes. No subscription, no recurring charges.</p>
            <BulletList items={[
              'Full rewrite of headline, summary, and experience sections',
              'Certified by a named, active recruiter from your domain',
              'Personalised LinkedIn Playbook included',
              '7 days of unlimited revisions after delivery',
              'Delivered as a PDF within 48 hours',
              '100-day Quality Guarantee — full refund if outcomes not met',
            ]} />
            <button onClick={() => navigate('/checkout')} style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: 'none', background: '#F59E0B', color: 'white', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              Buy the rewrite — ₹{pricing.price}
            </button>
          </Card>
        </div>

        <Card>
          <Section title="Pricing details">
            <BulletList items={[
              'Current price for the LinkedIn Rewrite + Recruiter Certification: ₹999 (Indian Rupees), one-time.',
              'The price displayed is the final amount payable — there are no hidden charges, processing fees, or shipping fees.',
              'All payments are processed in INR through our payment partner Razorpay. We do not store any card or payment credential on our servers.',
              'Pricing is dynamic and may change in future based on demand, seasonal offers, or service updates. Any change in price will only apply to new orders placed after the change. Orders already paid for are fully honoured at the price you paid.',
              'Discount codes and promotional offers, if any, will be visibly applied at checkout before you confirm payment.',
            ]} />
          </Section>

          <Section title="Taxes">
            <P>Prices on Successlane are <strong>inclusive of all applicable taxes</strong>. Success Lane is a small business currently exempt from GST registration because its annual turnover is below the threshold limit of ₹20 lakhs prescribed under Section 22 of the CGST Act, 2017. As such, no GST is separately levied on your purchase, and no GST invoice can be issued.</P>
            <P>If our turnover crosses the registration threshold in future, we will register for GST and update this policy and our invoicing accordingly.</P>
          </Section>

          <Section title="Refunds, cancellations &amp; delivery">
            <P>For details on when refunds apply, please see our <button onClick={() => navigate('/refund')} style={{ background: 'none', border: 'none', padding: 0, color: '#1A6FD6', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Refund Policy</button> and <button onClick={() => navigate('/cancellation')} style={{ background: 'none', border: 'none', padding: 0, color: '#1A6FD6', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Cancellation Policy</button>. For delivery details, see the <button onClick={() => navigate('/shipping')} style={{ background: 'none', border: 'none', padding: 0, color: '#1A6FD6', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Shipping &amp; Service Delivery Policy</button>.</P>
          </Section>
        </Card>

        <div style={{ borderTop: '1px solid #E5E9F0', paddingTop: '2rem', marginTop: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ color: '#9CA3AF', fontSize: '0.8125rem' }}>© 2025 Success Lane · Successlane</p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/refund')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Refund</button>
            <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Terms</button>
            <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Privacy</button>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '0.8125rem' }}>Back to Home</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
